import { PRODUCTS, ADDON_PRODUCTS, productNameMatches } from './config.js';
import { classifyNicknameLoose, priceMeta, productFromDisplayName } from './priceCatalog.js';
import { productFromSubscription } from './selectionFromSubscription.js';
import { isActiveSubscription } from './subscriptionStatuses.js';

const DEDICATED_ENV_NAME_RE = /dedic/i;

/** True for either DE kind (CFD `dedicated` or Futures `dedicatedFutures`). */
export function isDedicatedProduct(product) {
  return product === ADDON_PRODUCTS.dedicated || product === ADDON_PRODUCTS.dedicatedFutures;
}

function productForPriceId(priceId, catalog) {
  const price = catalog?.getById?.(priceId);
  if (!price) return null;
  // priceMeta is strict (drives selection). Fall back to the loose classifier so
  // a renamed legacy price (e.g. `standard_monthly_legacy`) still resolves for
  // existing-subscription counting without entering catalog selection.
  return priceMeta(price)?.product ?? classifyNicknameLoose(price.nickname)?.product ?? null;
}

/**
 * A subscription represents a Dedicated Environment if its PRIMARY product is
 * DE — i.e. a legacy solo-DE sub. Bundle subs that carry a DE line among other
 * products are NOT DE subs; their DE line is handled per-item by the router.
 * @param {object | null | undefined} sub
 * @returns {boolean}
 */
export function isDedicatedEnv(sub) {
  if (!sub) return false;
  const lines = sub.lineItems ?? sub.line_items ?? sub.lineItemsList;
  const isBundle = Array.isArray(lines) && lines.length > 1;
  if (isBundle) return false;
  if (sub.dedicatedNickName) return true;
  return DEDICATED_ENV_NAME_RE.test(String(sub.productName || ''));
}

// Legacy subs may still say "Whitelabel".
const SIGNAL_MANAGER_NAME_RE = /white ?label|signal[ _]?manager/i;

// The standalone Signal Manager org sub, by product name / price nickname (no is_wl flag).
const EA_HOSTING_NAME_RE = /ea[ _-]?(hosting|connect)/i;

/**
 * True when this subscription is EA Hosting. Like Signal Manager it is a
 * STANDALONE product: it grants no trading accounts and needs no copier plan
 * underneath it, so it must never anchor or seed the copier checkout.
 * @param {object} sub
 * @returns {boolean}
 */
export function isEaHosting(sub) {
  if (!sub) return false;
  if (EA_HOSTING_NAME_RE.test(String(sub.productName || sub.priceNickname || ''))) return true;
  return subscriptionLineItems(sub).some(
    (line) => EA_HOSTING_NAME_RE.test(String(line.productName || '')) || EA_HOSTING_NAME_RE.test(String(line.priceNickname || ''))
  );
}

export function isSignalManager(sub) {
  if (!sub) return false;
  if (SIGNAL_MANAGER_NAME_RE.test(String(sub.productName || sub.priceNickname || ''))) return true;
  return subscriptionLineItems(sub).some(isSignalManagerLine);
}

/** Whether one line of a subscription is a Signal Manager room. */
export function isSignalManagerLine(line) {
  return SIGNAL_MANAGER_NAME_RE.test(String(line.productName || '')) || SIGNAL_MANAGER_NAME_RE.test(String(line.priceNickname || ''));
}

/**
 * Normalized line items for a subscription (from line_items proto or legacy top-level fields).
 */
export function subscriptionLineItems(sub) {
  if (!sub) return [];

  // lineItemsList = raw proto AsObject (badge surfaces pass it un-normalized).
  const raw = sub.lineItems ?? sub.line_items ?? sub.lineItemsList;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((line) => ({
      subscriptionItemId: line.subscriptionItemId ?? line.subscription_item_id ?? '',
      priceId: line.priceId ?? line.price_id ?? '',
      productId: line.productId ?? line.product_id ?? '',
      productName: line.productName ?? line.product_name ?? '',
      priceNickname: line.priceNickname ?? line.price_nickname ?? '',
      quantity: Math.max(0, Number(line.quantity) || 0),
      unitAmount: Math.max(0, Number(line.unitAmount ?? line.unit_amount) || 0),
      dedicatedNickName: line.dedicatedNickName ?? line.dedicated_nick_name ?? ''
    }));
  }

  const qty = Math.max(0, Number(sub.quantity) || 0);
  if (qty <= 0 && !sub.productName && !sub.priceNickname) {
    return [];
  }

  return [
    {
      subscriptionItemId: sub.subscriptionItemId ?? sub.subscription_item_id ?? '',
      priceId: sub.priceId ?? sub.price_id ?? '',
      productId: '',
      productName: sub.productName ?? '',
      priceNickname: sub.priceNickname ?? '',
      quantity: qty,
      unitAmount: Math.max(0, Number(sub.unitAmount) || 0)
    }
  ];
}

/**
 * @param {object} line
 * @param {object | null} catalog
 * @param {object} [sub] fallback subscription for nickname inference
 */
export function lineItemProduct(line, catalog, sub) {
  if (!line) return null;

  if (line.priceId && catalog) {
    const fromPrice = productForPriceId(line.priceId, catalog);
    if (fromPrice) return fromPrice;
  }

  // Line-level info only. Falling back to sub.priceNickname / sub.productName
  // misclassifies non-primary lines of multi-line subs (e.g. a Futures line on
  // a "standard_monthly" sub would resolve to standard via the sub's nickname).
  if (line.priceNickname) {
    const meta = classifyNicknameLoose(line.priceNickname);
    if (meta?.product) return meta.product;
  }

  const byName = productFromDisplayName(line.productName);
  if (byName) return byName;

  // Last-resort sub fallback (only reliable for single-line subs).
  return productFromSubscription(sub);
}

/**
 * Returns every DE line item on a subscription. Empty array for non-DE subs.
 */
export function dedicatedEnvLinesOf(sub, catalog) {
  const out = [];
  if (!sub) return out;
  for (const line of subscriptionLineItems(sub)) {
    if (isDedicatedProduct(lineItemProduct(line, catalog, sub))) {
      out.push(line);
    }
  }
  return out;
}

/**
 * @param {object} sub
 * @param {string} product
 * @param {object | null} catalog
 */
export function findLineItemForProduct(sub, product, catalog) {
  if (!sub || !product) return null;
  for (const line of subscriptionLineItems(sub)) {
    if (lineItemProduct(line, catalog, sub) === product) {
      return line;
    }
  }
  return null;
}

/**
 * Every line item on a sub that classifies to `product`. Usually one, but a
 * grandfathered user can carry two (legacy + new price); the router sums these.
 * @returns {object[]}
 */
export function findLineItemsForProduct(sub, product, catalog) {
  if (!sub || !product) return [];
  const out = [];
  for (const line of subscriptionLineItems(sub)) {
    if (lineItemProduct(line, catalog, sub) === product) {
      out.push(line);
    }
  }
  return out;
}

/**
 * Sum seat counts per copier product across active subscriptions.
 * @param {object[]} subscriptions
 * @param {object | null} catalog
 */
export function copierQuantitiesFromSubscriptions(subscriptions, catalog) {
  const counts = {
    [PRODUCTS.standard]: 0,
    [PRODUCTS.premium]: 0,
    [PRODUCTS.futures]: 0
  };

  for (const sub of subscriptions || []) {
    if (!isActiveSubscription(sub)) continue;

    const lines = subscriptionLineItems(sub);
    if (lines.length > 0) {
      for (const line of lines) {
        const product = lineItemProduct(line, catalog, sub);
        if (product && Object.prototype.hasOwnProperty.call(counts, product)) {
          counts[product] += line.quantity;
        }
      }
      continue;
    }

    const product = productFromSubscription(sub);
    const qty = Math.max(0, Number(sub.quantity) || 0);
    if (product && Object.prototype.hasOwnProperty.call(counts, product)) {
      counts[product] += qty;
    }
  }

  return counts;
}

/**
 * Whether any active subscription includes this product (including line_items).
 * @param {object[]} subscriptions
 * @param {string} product
 * @param {object | null} [catalog]
 */
export function subscriptionIncludesProduct(subscriptions, product, catalog) {
  if (!product) return false;

  for (const sub of subscriptions || []) {
    if (!isActiveSubscription(sub)) continue;

    for (const line of subscriptionLineItems(sub)) {
      if (lineItemProduct(line, catalog, sub) === product) return true;
    }

    if (productFromSubscription(sub) === product) return true;

    // Name fallback for subs whose line items do not carry the product.
    // One matcher for every product (config.productNameMatches) — this used to
    // be a regex per product here, drifting from the copies in priceCatalog and
    // addonEntitlements.
    if (product === ADDON_PRODUCTS.dedicated && sub.dedicatedNickName) return true;
    if (productNameMatches(product, sub.productName)) return true;
  }

  return false;
}
